import { useEffect, useRef } from "react";
import styles from "../styles/coastscene.module.css";

// A sunlit coastal horizon in flat ligne-claire: a Hopper sky over a
// small Mediterranean hill-town, hard flat shadow planes, swells and
// foam on the water, a little sloop.
//
// `embed` mode drops the scene's own sky (and sun/clouds) so it can sit
// at the bottom of a sky-blue section — the section's blue becomes the
// sky, and the town + boat gather on the right. The boat still rocks.
export default function CoastScene({ className, embed = false }) {
    const skyRef = useRef(null);
    const seaRef = useRef(null);

    useEffect(() => {
        if (embed) return; // embedded scene keeps only its ambient motion
        const reduce = window.matchMedia &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduce) return;

        let raf = 0;
        const onScroll = () => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                raf = 0;
                const s = Math.min(window.scrollY || 0, 600);
                if (skyRef.current) {
                    skyRef.current.style.transform = `translate(${s * 0.04}px, ${-s * 0.05}px)`;
                }
                if (seaRef.current) {
                    seaRef.current.style.transform = `translate(${s * 0.08}px, 0px)`;
                }
            });
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [embed]);

    // in embed mode the town and boat gather on the right of the text
    const townShift = embed ? "translate(250 0)" : undefined;
    const boatShift = embed ? "translate(150 0)" : undefined;

    return (
        <svg
            className={className}
            viewBox="0 0 1200 440"
            preserveAspectRatio="xMidYMax slice"
            role="img"
            aria-label="A sunlit coast: a small hill-town on the far shore under a big sky, with a sailboat on the swell."
        >
            <defs>
                {/* the one sanctioned gradient — a subtle sky wash */}
                <linearGradient id="skywash" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--sky)" />
                    <stop offset="100%" stopColor="var(--sky-pale)" />
                </linearGradient>
            </defs>

            {/* sky is drawn only when the scene owns its own sky */}
            {!embed && <rect x="0" y="0" width="1200" height="302" fill="url(#skywash)" />}
            <rect x="0" y="300" width="1200" height="140" fill="var(--seafoam)" />
            {/* a slightly deeper band of water just below the horizon */}
            <rect x="0" y="300" width="1200" height="30" fill="#96CDBF" />

            {/* ---------- sky layer (parallax) ---------- */}
            <g ref={skyRef} className={styles.skyLayer}>
                {!embed && (
                    <>
                        {/* high sun, off-center left */}
                        <circle className={styles.sun} cx="196" cy="118" r="46" fill="var(--butter)" stroke="var(--ink)" strokeWidth="3" />

                        {/* wispy cirrus */}
                        <g stroke="var(--cloud)" strokeWidth="8" strokeLinecap="round" fill="none" opacity="0.85">
                            <path d="M840 96 q60 -16 130 -4 t150 2" />
                            <path d="M910 128 q50 -12 110 -2 t120 4" />
                            <path d="M980 158 q40 -10 90 -2" />
                        </g>
                    </>
                )}

                {/* gulls */}
                {!embed && (
                    <g className={styles.birds} stroke="var(--ink)" strokeWidth="2.5" strokeLinecap="round" fill="none">
                        <path d="M372 108 q12 -11 24 0 q12 -11 24 0" />
                        <path d="M436 90 q10 -9 20 0 q10 -9 20 0" />
                        <path d="M470 128 q8 -7 16 0 q8 -7 16 0" />
                    </g>
                )}

                {!embed && (
                    <>
                        {/* small distant cumulus */}
                        <path
                            d="M300 176 C284 176 279 158 293 152 C289 135 314 128 325 140 C331 124 358 124 364 142 C383 132 405 145 398 162 C414 165 414 178 398 177 Z"
                            fill="var(--cloud)" stroke="var(--ink)" strokeWidth="2.5" strokeLinejoin="round"
                        />
                        {/* main cumulus with flat shaded underside */}
                        <g className={styles.cloud}>
                            <path
                                d="M726 214 C700 214 691 188 713 178 C706 150 748 138 766 158 C776 132 824 132 834 160 C866 144 908 162 898 194 C924 197 926 216 900 214 Z"
                                fill="var(--cloud)" stroke="var(--ink)" strokeWidth="2.5" strokeLinejoin="round"
                            />
                            <path
                                d="M713 208 C742 214 875 214 899 208 C900 214 894 216 884 214 C742 214 728 214 716 214 C708 214 708 210 713 208 Z"
                                fill="#EBE4D3"
                            />
                        </g>
                    </>
                )}
            </g>

            {/* ---------- horizon ---------- */}
            <line x1="0" y1="300" x2="1200" y2="300" stroke="var(--ink)" strokeWidth="2" opacity="0.4" />

            {/* ---------- the hill-town ---------- */}
            <g stroke="var(--ink)" strokeWidth="2.5" strokeLinejoin="round" transform={townShift}>
                {/* cypress trees, left */}
                <path d="M352 300 C344 274 348 246 360 240 C372 246 376 274 368 300 Z" fill="var(--sage-deep)" stroke="none" />
                <path d="M334 300 C328 280 331 258 340 253 C349 258 352 280 346 300 Z" fill="var(--sage)" stroke="none" />

                {/* house 1 — flat roof */}
                <rect x="382" y="216" width="74" height="84" fill="var(--cloud)" />
                <rect x="438" y="216" width="18" height="84" fill="rgba(38,48,59,0.14)" stroke="none" />
                <rect x="396" y="234" width="16" height="20" fill="var(--butter)" strokeWidth="1.5" />
                <rect x="422" y="234" width="16" height="20" fill="var(--cloud)" strokeWidth="1.5" />
                <rect x="396" y="264" width="16" height="20" fill="var(--cloud)" strokeWidth="1.5" />
                <rect x="422" y="264" width="16" height="20" fill="var(--cloud)" strokeWidth="1.5" />

                {/* campanile */}
                <rect x="462" y="158" width="34" height="142" fill="var(--cloud)" />
                <rect x="484" y="158" width="12" height="142" fill="rgba(38,48,59,0.14)" stroke="none" />
                <path d="M458 158 L479 140 L500 158 Z" fill="var(--terracotta)" />
                <path d="M470 182 a9 9 0 0 1 18 0 v16 h-18 Z" fill="var(--ink)" stroke="none" />

                {/* house 2 — pitched terracotta roof */}
                <rect x="500" y="208" width="86" height="92" fill="var(--cloud)" />
                <rect x="568" y="208" width="18" height="92" fill="rgba(38,48,59,0.14)" stroke="none" />
                <path d="M494 208 L543 176 L592 208 Z" fill="var(--terracotta)" />
                <rect x="514" y="228" width="18" height="22" fill="var(--cloud)" strokeWidth="1.5" />
                <rect x="546" y="228" width="18" height="22" fill="var(--butter)" strokeWidth="1.5" />
                <rect x="530" y="266" width="22" height="34" fill="var(--wood)" strokeWidth="1.5" />

                {/* house 3 — domed */}
                <rect x="594" y="222" width="72" height="78" fill="var(--cloud)" />
                <rect x="648" y="222" width="18" height="78" fill="rgba(38,48,59,0.14)" stroke="none" />
                <path d="M594 222 a36 36 0 0 1 72 0 Z" fill="var(--cloud)" />
                <path d="M604 220 a26 26 0 0 1 26 -24" fill="none" stroke="var(--butter)" strokeWidth="5" strokeLinecap="round" />
                <rect x="610" y="244" width="16" height="20" fill="var(--cloud)" strokeWidth="1.5" />
                <rect x="636" y="244" width="16" height="20" fill="var(--cloud)" strokeWidth="1.5" />

                {/* house 4 — pitched wood roof */}
                <rect x="672" y="230" width="60" height="70" fill="var(--cloud)" />
                <rect x="716" y="230" width="16" height="70" fill="rgba(38,48,59,0.14)" stroke="none" />
                <path d="M666 230 L702 204 L738 230 Z" fill="var(--wood)" />
                <rect x="684" y="248" width="16" height="18" fill="var(--butter)" strokeWidth="1.5" />

                {/* house 5 — short flat roof */}
                <rect x="740" y="250" width="42" height="50" fill="var(--cloud)" />
                <rect x="770" y="250" width="12" height="50" fill="rgba(38,48,59,0.14)" stroke="none" />

                {/* cypress, right */}
                <path d="M796 300 C790 278 793 254 803 249 C813 254 816 278 810 300 Z" fill="var(--sage-deep)" stroke="none" />
            </g>

            {/* town reflection */}
            {!embed && (
                <g stroke="#FBF8F0" strokeWidth="3" opacity="0.3" strokeLinecap="round">
                    <line x1="420" y1="304" x2="420" y2="316" />
                    <line x1="479" y1="304" x2="479" y2="320" />
                    <line x1="543" y1="304" x2="543" y2="318" />
                    <line x1="629" y1="304" x2="629" y2="315" />
                    <line x1="702" y1="304" x2="702" y2="314" />
                </g>
            )}

            {/* ---------- sea layer (parallax) ---------- */}
            <g ref={seaRef} className={styles.seaLayer}>
                {/* swells — flat darker ribbons */}
                <path d="M0 346 q120 -14 240 0 t240 0 t240 0 t240 0 t240 0 v12 q-120 14 -240 0 t-240 0 t-240 0 t-240 0 t-240 0 Z" fill="#8FC8BA" opacity="0.7" />
                <path d="M0 398 q140 -16 280 0 t280 0 t280 0 t280 0 v14 q-140 16 -280 0 t-280 0 t-280 0 t-280 0 Z" fill="#8FC8BA" opacity="0.55" />

                {/* ink swell lines + foam caps */}
                <g stroke="var(--ink)" strokeWidth="1.5" fill="none" opacity="0.18">
                    <path d="M60 330 q60 -8 120 0 t120 0" />
                    <path d="M840 336 q60 -8 120 0 t120 0" />
                    <path d="M300 372 q70 -9 140 0 t140 0" />
                </g>
                <g stroke="var(--ink)" strokeWidth="2" fill="var(--cloud)">
                    <path d="M250 356 q10 -8 20 0 q-4 6 -10 6 q-6 0 -10 -6 Z" />
                    <path d="M1010 366 q10 -8 20 0 q-4 6 -10 6 q-6 0 -10 -6 Z" />
                </g>

                {/* the little sloop */}
                <g transform={boatShift}>
                    <g className={styles.boat} stroke="var(--ink)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round">
                        <line x1="918" y1="300" x2="918" y2="346" />
                        <path d="M918 304 L956 344 L918 344 Z" fill="var(--cloud)" />
                        <path d="M914 306 L884 344 L914 344 Z" fill="#F2EEE1" />
                        <path d="M876 344 L968 344 Q972 356 958 360 L886 360 Q874 356 876 344 Z" fill="var(--wood)" />
                    </g>
                </g>
            </g>
        </svg>
    );
}
